/**
 * Splits an interval in seconds into a whole number of the largest unit that
 * divides it evenly.
 *
 *   900   -> { value: 15, unit: 'minute' }
 *   3600  -> { value: 1,  unit: 'hour' }
 *   5400  -> { value: 90, unit: 'minute' }   (not a whole number of hours)
 *   86400 -> { value: 24, unit: 'hour' }
 *
 * @param seconds
 * @returns {{value: number, unit: string}|null} null when seconds isn't a usable number
 */
export const intervalParts = seconds => {

  const secs = parseInt(seconds, 10);

  if(!Number.isFinite(secs) || secs <= 0) {
    return null;
  }

  if(secs >= 3600 && secs % 3600 === 0) {
    return { value: secs / 3600, unit: 'hour' };
  }

  if(secs < 60) {
    return { value: secs, unit: 'second' };
  }

  return { value: Math.round(secs / 60), unit: 'minute' };

};

/**
 * Renders an interval in seconds as whole minutes or whole hours.
 *
 *   900   -> "15 minutes"
 *   3600  -> "1 hour"
 *   5400  -> "90 minutes"   (not a whole number of hours)
 *   86400 -> "24 hours"
 *
 * @param seconds
 * @param fallback returned when seconds isn't a usable number
 * @returns {string}
 */
const formatInterval = (seconds, fallback = '—') => {

  const parts = intervalParts(seconds);

  if(!parts) {
    return fallback;
  }

  return `${parts.value} ${parts.unit}${parts.value === 1 ? '' : 's'}`;

};

/**
 * Short form of the interval for table cells.
 *
 *   900    -> "15 min"
 *   3600   -> "1 hr"
 *   172800 -> "48 hr"
 *
 * @param seconds
 * @param fallback returned when seconds isn't a usable number
 * @returns {string}
 */
export const formatIntervalShort = (seconds, fallback = '—') => {

  const parts = intervalParts(seconds);

  if(!parts) {
    return fallback;
  }

  const unit = { hour: 'hr', minute: 'min', second: 'sec' }[parts.unit];

  return `${parts.value} ${unit}`;

};

/**
 * Middle-length form: full "hour(s)", abbreviated "min(s)" / "sec(s)".
 *
 *   900   -> "15 mins"
 *   3600  -> "1 hour"
 *   7200  -> "2 hours"
 *
 * @param seconds
 * @param fallback returned when seconds isn't a usable number
 * @returns {string}
 */
export const formatIntervalMedium = (seconds, fallback = '—') => {

  const parts = intervalParts(seconds);

  if(!parts) {
    return fallback;
  }

  const unit = { hour: 'hour', minute: 'min', second: 'sec' }[parts.unit];

  return `${parts.value} ${unit}${parts.value === 1 ? '' : 's'}`;

};

export default formatInterval;
